// src/pages/ChatComunitario.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    collection,
    doc,
    getDocs,
    onSnapshot,
    orderBy,
    query,
    runTransaction,
    serverTimestamp,
    updateDoc,
    where,
} from 'firebase/firestore';
import { FaChevronLeft, FaCircle, FaPaperPlane, FaSearch, FaUserCircle } from 'react-icons/fa';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import './ChatComunitario.css';
import './ChatComunitarioMobile.css';

const ADMIN_ROLES = ['admin', 'admin_fracc', 'administrador', 'manager', 'platformadmin', 'plataforma', 'superadmin'];
const CASETA_ROLES = ['caseta', 'guardia', 'security'];
const ONLINE_THRESHOLD_MS = 2 * 60 * 1000;
const AVATAR_PALETTE = ['#6f47eb', '#3b82f6', '#ec4899', '#f97316', '#22c55e', '#a855f7', '#0ea5e9', '#fbbf24'];

const normalizeRole = (raw) => String(raw || '').toLowerCase();
const isAdminRole = (role) => ADMIN_ROLES.includes(normalizeRole(role));
const isCasetaRole = (role) => CASETA_ROLES.includes(normalizeRole(role));
const buildConversationId = (a, b) => [a, b].sort().join('__');
const pickFirst = (...values) => values.find((value) => {
    if (typeof value === 'string') {
        return value.trim().length > 0;
    }
    if (typeof value === 'number') {
        return true;
    }
    return false;
});
const safeTrim = (value) => (typeof value === 'string' ? value.trim() : '');
const getAvatarColor = (id) => {
    if (!id) {
        return AVATAR_PALETTE[0];
    }
    let hash = 0;
    for (let index = 0; index < id.length; index += 1) {
        hash = (hash << 5) - hash + id.charCodeAt(index);
        hash |= 0;
    }
    const paletteIndex = Math.abs(hash) % AVATAR_PALETTE.length;
    return AVATAR_PALETTE[paletteIndex];
};
const toDateSafe = (value) => {
    if (!value) {
        return null;
    }
    if (value.toDate) {
        try {
            return value.toDate();
        } catch (error) {
            return null;
        }
    }
    if (value instanceof Date) {
        return value;
    }
    if (typeof value === 'number') {
        return new Date(value);
    }
    return null;
};
const computeInitials = (name) => {
    if (!name) {
        return '??';
    }
    const segments = name.split(/\s+/).filter(Boolean);
    if (segments.length === 0) {
        return '??';
    }
    if (segments.length === 1) {
        return segments[0].substring(0, 2).toUpperCase();
    }
    return (segments[0][0] + segments[segments.length - 1][0]).toUpperCase();
};
const getDisplayName = (user) => {
    const name = pickFirst(
        user?.nombre,
        user?.nombreCompleto,
        `${safeTrim(user?.primerNombre)} ${safeTrim(user?.apellidoPaterno)}`.trim(),
        user?.email,
    );
    if (!name) {
        return 'Usuario';
    }
    return String(name).replace(/\s+/g, ' ').trim();
};
const deriveEmail = (user) => {
    const email = pickFirst(user?.email, user?.correo, user?.correoElectronico);
    return email ? String(email).trim() : '';
};
const formatHouseLabel = (user) => {
    if (isAdminRole(user?.rol)) {
        return 'Casa Admin';
    }
    const candidate = pickFirst(
        user?.casaNombre,
        user?.casaNumero,
        user?.numeroCasa,
        user?.casa,
        user?.casaCodigo,
        user?.casaId,
        user?.identificadorCasa,
        user?.residente?.casaNumero,
        user?.residente?.numeroCasa,
        user?.direccionCorta,
        user?.direccion,
    );
    if (!candidate) {
        return 'Casa sin asignar';
    }
    const text = String(candidate).trim();
    if (!text) {
        return 'Casa sin asignar';
    }
    const normalized = text.toLowerCase().startsWith('casa') ? text : `Casa ${text}`;
    return normalized.replace(/\s+/g, ' ').replace(/(^|\s)\S/g, (char) => char.toUpperCase());
};
const isOnline = (presence) => {
    const state = typeof presence?.state === 'string' ? presence.state.toLowerCase() : '';
    if (state !== 'online') {
        return false;
    }
    const updatedAt = toDateSafe(presence?.updatedAt);
    if (!updatedAt) {
        return false;
    }
    return Date.now() - updatedAt.getTime() <= ONLINE_THRESHOLD_MS;
};
const relativeTimeFrom = (date) => {
    if (!date) {
        return 'Fuera de línea';
    }
    const diff = Date.now() - date.getTime();
    if (diff < 60_000) {
        return 'Hace un momento';
    }
    if (diff < 3_600_000) {
        const minutes = Math.floor(diff / 60_000);
        return `Hace ${minutes} min`;
    }
    if (diff < 86_400_000) {
        const hours = Math.floor(diff / 3_600_000);
        return `Hace ${hours} h`;
    }
    return date.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
};
const formatMessageTime = (timestamp) => {
    const date = toDateSafe(timestamp);
    if (!date) {
        return '';
    }
    return date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
};

const ChatComunitario = () => {
    const { currentUser, userData } = useAuth();
    const [rawUsers, setRawUsers] = useState({});
    const [threads, setThreads] = useState({});
    const [search, setSearch] = useState('');
    const [selectedContactId, setSelectedContactId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [messageText, setMessageText] = useState('');
    const messagesEndRef = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();
    const textareaRef = useRef(null);
    const messagesContainerRef = useRef(null);

    const fraccionamientoId = userData?.fraccionamientoId || userData?.fraccionamientoTd || null;
    const currentUid = currentUser?.uid || null;
    const normalizedRole = normalizeRole(userData?.rol);
    // Nota: El selector de fraccionamiento ha sido removido para superadmin
    // Ahora el chat usa el fraccionamientoId asignado al usuario

    // useEffect para cargar usuarios del fraccionamiento asignado
    useEffect(() => {
        setRawUsers({});
        if (!fraccionamientoId) {
            return undefined;
        }

        const usuariosRef = collection(db, 'usuarios');
        let cancelled = false;
        let unsubscribePrimary = null;
        let unsubscribeLegacy = null;

        (async () => {
            try {
                // Cargar todos los usuarios que pertenecen a este fraccionamiento
                // Preferir fraccionamientoId, pero también incluir legacy fraccionamientoTd
                const primaryQuery = query(usuariosRef, where('fraccionamientoId', '==', fraccionamientoId));
                const legacyQuery = query(usuariosRef, where('fraccionamientoTd', '==', fraccionamientoId));
                const primarySnapshot = await getDocs(primaryQuery);

                if (cancelled) {
                    return;
                }

                const loadedIds = new Set();
                const initialUsers = {};

                // Agregar usuarios de la query principal
                primarySnapshot.forEach((docSnapshot) => {
                    const docId = docSnapshot.id;
                    loadedIds.add(docId);
                    initialUsers[docId] = { id: docId, ...docSnapshot.data() };
                });

                // Cargar usuarios legacy que NO estén ya en la query principal
                const legacySnapshot = await getDocs(legacyQuery);

                if (cancelled) {
                    return;
                }

                legacySnapshot.forEach((docSnapshot) => {
                    const docId = docSnapshot.id;
                    // Solo incluir si no fue cargado en la query principal
                    if (!loadedIds.has(docId)) {
                        const data = docSnapshot.data();
                        // Verificar que no tiene fraccionamientoId (para evitar duplicados)
                        if (!data.fraccionamientoId) {
                            loadedIds.add(docId);
                            initialUsers[docId] = { id: docId, ...data };
                        }
                    }
                });

                if (!cancelled) {
                    setRawUsers(initialUsers);
                }

                // Ahora escuchar cambios en tiempo real para los usuarios cargados
                if (!cancelled) {
                    unsubscribePrimary = onSnapshot(
                        primaryQuery,
                        (snapshot) => {
                            setRawUsers((prev) => {
                                const updated = { ...prev };
                                snapshot.docChanges().forEach((change) => {
                                    const docId = change.doc.id;
                                    if (change.type === 'removed') {
                                        delete updated[docId];
                                    } else {
                                        updated[docId] = { id: docId, ...change.doc.data() };
                                    }
                                });
                                return updated;
                            });
                        },
                        (error) => {
                            if (!cancelled) {
                                console.error('Error en listener primary chat', error);
                            }
                        }
                    );

                    unsubscribeLegacy = onSnapshot(
                        legacyQuery,
                        (snapshot) => {
                            setRawUsers((prev) => {
                                const updated = { ...prev };
                                snapshot.docChanges().forEach((change) => {
                                    const docId = change.doc.id;
                                    if (change.type === 'removed') {
                                        delete updated[docId];
                                    } else {
                                        updated[docId] = { id: docId, ...change.doc.data() };
                                    }
                                });
                                return updated;
                            });
                        },
                        (error) => {
                            if (!cancelled) {
                                console.error('Error en listener legacy chat', error);
                            }
                        }
                    );
                }
            } catch (error) {
                if (!cancelled) {
                    console.error('Error cargando usuarios para chat', error);
                }
            }
        })();

        return () => {
            cancelled = true;
            if (unsubscribePrimary) {
                unsubscribePrimary();
            }
            if (unsubscribeLegacy) {
                unsubscribeLegacy();
            }
        };
    }, [fraccionamientoId]);


    const currentUserDoc = useMemo(() => {
        if (!currentUid) {
            return null;
        }
        return rawUsers[currentUid] || { ...userData, id: currentUid };
    }, [currentUid, rawUsers, userData]);

    const currentUserName = useMemo(() => getDisplayName(currentUserDoc || {}), [currentUserDoc]);
    const currentUserHouse = useMemo(() => formatHouseLabel(currentUserDoc || {}), [currentUserDoc]);

    useEffect(() => {
        setSelectedContactId(null);
        setMessages([]);
        setSearch('');
    }, [fraccionamientoId]);

    const contactCandidates = useMemo(() => {
        if (!currentUid || !fraccionamientoId) {
            return [];
        }
        return Object.values(rawUsers)
            .filter((user) => {
                if (!user || user.id === currentUid || user.activo === false) {
                    return false;
                }
                // Excluir usuarios con rol de caseta
                const role = normalizeRole(user.rol);
                return !isCasetaRole(role);
            })
            .map((user) => {
                const role = normalizeRole(user.rol);
                const displayName = getDisplayName(user);
                const houseLabel = formatHouseLabel(user);
                const presence = user?.chatPresence || null;
                const lastPresence = toDateSafe(presence?.updatedAt);
                return {
                    id: user.id,
                    displayName,
                    houseLabel,
                    normalizedRole: role,
                    isAdmin: isAdminRole(role),
                    email: deriveEmail(user),
                    avatarColor: getAvatarColor(user.id),
                    initials: computeInitials(displayName),
                    isOnline: isOnline(presence),
                    lastPresence,
                };
            });
    }, [fraccionamientoId, currentUid, rawUsers]);

    useEffect(() => {
        if (!fraccionamientoId || !currentUid) {
            setThreads({});
            return undefined;
        }
        const chatsRef = collection(db, 'fraccionamientos', fraccionamientoId, 'chats');
        const q = query(chatsRef, where('participantIds', 'array-contains', currentUid));
        return onSnapshot(q, (snapshot) => {
            const map = {};
            snapshot.forEach((docSnapshot) => {
                map[docSnapshot.id] = { id: docSnapshot.id, ...docSnapshot.data() };
            });
            setThreads(map);
        }, (error) => {
            console.error('No se pudo obtener los chats del usuario', error);
            setThreads({});
        });
    }, [fraccionamientoId, currentUid]);

    const processedContacts = useMemo(() => {
        const term = search.trim().toLowerCase();
        return contactCandidates
            .map((contact) => {
                const conversationId = buildConversationId(currentUid, contact.id);
                const thread = threads[conversationId];
                const lastMessageAt = toDateSafe(thread?.lastMessageAt);
                const unreadCount = Number(thread?.unreadCounts?.[currentUid] || 0);
                const lastMessageText = thread?.lastMessageText || '';
                return {
                    ...contact,
                    conversationId,
                    unreadCount,
                    lastMessageAt,
                    lastMessageText,
                    statusLabel: contact.isOnline ? 'En línea' : relativeTimeFrom(contact.lastPresence),
                };
            })
            .filter((contact) => {
                if (!term) {
                    return true;
                }
                const haystack = `${contact.displayName} ${contact.houseLabel}`.toLowerCase();
                return haystack.includes(term);
            });
    }, [contactCandidates, currentUid, search, threads]);

    // Lógica para Deep Linking (abrir chat desde notificación)
    useEffect(() => {
        if (!processedContacts.length) return;

        const params = new URLSearchParams(location.search);
        const contactId = params.get('contactId');
        const conversationId = params.get('conversationId');

        if (contactId) {
            const found = processedContacts.find(c => c.id === contactId);
            if (found) {
                setSelectedContactId(found.id);
                // Limpiar el parámetro para evitar re-selección indeseada
                navigate(location.pathname, { replace: true });
            }
        } else if (conversationId) {
            const found = processedContacts.find(c => c.conversationId === conversationId);
            if (found) {
                setSelectedContactId(found.id);
                navigate(location.pathname, { replace: true });
            }
        }
    }, [location.search, processedContacts, navigate, location.pathname]);

    const sortContacts = useCallback((list) => {
        return [...list].sort((a, b) => {
            if (a.isAdmin && !b.isAdmin) {
                return -1;
            }
            if (!a.isAdmin && b.isAdmin) {
                return 1;
            }
            const timeA = a.lastMessageAt ? a.lastMessageAt.getTime() : 0;
            const timeB = b.lastMessageAt ? b.lastMessageAt.getTime() : 0;
            if (timeA !== timeB) {
                return timeB - timeA;
            }
            return a.displayName.localeCompare(b.displayName, 'es');
        });
    }, []);

    const onlineContacts = useMemo(
        () => sortContacts(processedContacts.filter((contact) => contact.isOnline)),
        [processedContacts, sortContacts],
    );
    const offlineContacts = useMemo(
        () => sortContacts(processedContacts.filter((contact) => !contact.isOnline)),
        [processedContacts, sortContacts],
    );

    useEffect(() => {
        if (!selectedContactId) {
            return;
        }
        const exists = processedContacts.some((contact) => contact.id === selectedContactId);
        if (!exists) {
            setSelectedContactId(null);
        }
    }, [processedContacts, selectedContactId]);

    const selectedContact = useMemo(
        () => processedContacts.find((contact) => contact.id === selectedContactId) || null,
        [processedContacts, selectedContactId],
    );

    const selectedConversationId = selectedContact ? selectedContact.conversationId : null;

    useEffect(() => {
        if (!selectedConversationId || !fraccionamientoId) {
            setMessages([]);
            return undefined;
        }
        const chatDocRef = doc(db, 'fraccionamientos', fraccionamientoId, 'chats', selectedConversationId);
        const messagesRef = collection(chatDocRef, 'messages');
        const ordered = query(messagesRef, orderBy('createdAt', 'asc'));
        return onSnapshot(ordered, (snapshot) => {
            const items = snapshot.docs.map((docSnapshot) => ({ id: docSnapshot.id, ...docSnapshot.data() }));
            setMessages(items);
        });
    }, [fraccionamientoId, selectedConversationId]);

    // Función para hacer scroll suave solo dentro del contenedor de mensajes
    const scrollToBottom = useCallback(() => {
        if (messagesContainerRef.current) {
            // Hacer scroll solo dentro del contenedor, no en toda la página
            const container = messagesContainerRef.current;
            requestAnimationFrame(() => {
                container.scrollTop = container.scrollHeight;
            });
        }
    }, []);

    useEffect(() => {
        // Solo hacer scroll cuando se carga una conversación o se envía un mensaje nuevo
        // NO hacer scroll automático al escribir para evitar vibración
        if (messages.length > 0) {
            // Usar un pequeño delay para asegurar que el DOM se haya actualizado
            setTimeout(() => {
                scrollToBottom();
            }, 100);
        }
    }, [messages, selectedConversationId, scrollToBottom]);

    // NO hacer scroll automático al enfocar para evitar vibración y pérdida del textarea
    // El scroll solo se hará cuando se carga una conversación o se envía un mensaje

    useEffect(() => {
        if (!selectedConversationId || !fraccionamientoId || !currentUid) {
            return;
        }
        const thread = threads[selectedConversationId];
        if (!thread) {
            return;
        }
        const unreadCount = Number(thread?.unreadCounts?.[currentUid] || 0);
        if (unreadCount <= 0) {
            return;
        }
        const chatDocRef = doc(db, 'fraccionamientos', fraccionamientoId, 'chats', selectedConversationId);
        updateDoc(chatDocRef, {
            [`unreadCounts.${currentUid}`]: 0,
            [`lastReadAt.${currentUid}`]: serverTimestamp(),
        }).catch((error) => {
            console.error('No se pudo marcar el chat como leído', error);
        });
    }, [fraccionamientoId, currentUid, selectedConversationId, threads]);

    const handleSendMessage = useCallback((event) => {
        event.preventDefault();
        if (!selectedContact || !currentUid || !fraccionamientoId) {
            return;
        }
        const text = messageText.trim();
        if (!text) {
            return;
        }
        const conversationId = selectedContact.conversationId;
        const chatDocRef = doc(db, 'fraccionamientos', fraccionamientoId, 'chats', conversationId);
        const messagesRef = collection(chatDocRef, 'messages');
        const currentSummary = {
            nombre: currentUserName,
            casa: currentUserHouse,
            rol: normalizedRole,
            email: deriveEmail(currentUserDoc || {}),
        };
        const contactSummary = {
            nombre: selectedContact.displayName,
            casa: selectedContact.houseLabel,
            rol: selectedContact.normalizedRole,
            email: selectedContact.email,
        };
        runTransaction(db, async (transaction) => {
            const snapshot = await transaction.get(chatDocRef);
            const now = serverTimestamp();
            if (!snapshot.exists()) {
                transaction.set(chatDocRef, {
                    participantIds: [currentUid, selectedContact.id].sort(),
                    participantSummaries: {
                        [currentUid]: currentSummary,
                        [selectedContact.id]: contactSummary,
                    },
                    fraccionamientoId: fraccionamientoId,
                    type: 'direct',
                    createdAt: now,
                    updatedAt: now,
                    lastMessageText: text,
                    lastMessageAt: now,
                    lastSenderId: currentUid,
                    unreadCounts: {
                        [currentUid]: 0,
                        [selectedContact.id]: 1,
                    },
                    lastReadAt: {
                        [currentUid]: now,
                    },
                });
            } else {
                const data = snapshot.data() || {};
                const unreadCounts = {
                    ...(data.unreadCounts || {}),
                    [currentUid]: 0,
                    [selectedContact.id]: Number((data.unreadCounts || {})[selectedContact.id] || 0) + 1,
                };
                const lastReadAt = {
                    ...(data.lastReadAt || {}),
                    [currentUid]: now,
                };
                transaction.update(chatDocRef, {
                    participantSummaries: {
                        ...(data.participantSummaries || {}),
                        [currentUid]: currentSummary,
                        [selectedContact.id]: contactSummary,
                    },
                    updatedAt: now,
                    lastMessageText: text,
                    lastMessageAt: now,
                    lastSenderId: currentUid,
                    unreadCounts,
                    lastReadAt,
                });
            }
            const newMessageRef = doc(messagesRef);
            transaction.set(newMessageRef, {
                senderId: currentUid,
                text,
                createdAt: now,
            });
        }).then(() => {
            setMessageText('');
            // Resetear altura del textarea después de enviar
            if (textareaRef.current) {
                textareaRef.current.style.height = '48px';
            }
            // Hacer scroll al final solo cuando se envía un mensaje nuevo, dentro del contenedor
            setTimeout(() => {
                scrollToBottom();
            }, 150);
        }).catch((error) => {
            console.error('No se pudo enviar el mensaje', error);
        });
    }, [fraccionamientoId, currentUid, currentUserDoc, currentUserHouse, currentUserName, messageText, normalizedRole, selectedContact]);

    const handleKeyDown = useCallback((event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSendMessage(event);
        }
    }, [handleSendMessage]);

    // Auto-ajustar altura del textarea (optimizado para evitar vibración)
    const adjustTextareaHeight = useCallback(() => {
        if (textareaRef.current) {
            // Usar requestAnimationFrame para suavizar los cambios y evitar vibración
            requestAnimationFrame(() => {
                if (textareaRef.current) {
                    // Guardar la altura actual para comparar
                    const currentHeight = textareaRef.current.style.height;

                    // Resetear altura para obtener el scrollHeight correcto
                    textareaRef.current.style.height = 'auto';
                    const scrollHeight = textareaRef.current.scrollHeight;
                    const maxHeight = 150; // Máximo de ~7-8 líneas (aumentado de 120)
                    const minHeight = 48; // Altura mínima (aumentado de 36)
                    const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);

                    // Solo actualizar si la altura cambió para evitar reflows innecesarios
                    if (currentHeight !== `${newHeight}px`) {
                        textareaRef.current.style.height = `${newHeight}px`;
                    }

                    // NO hacer scroll automático al escribir para evitar vibración y pérdida del textarea
                    // El scroll solo se hará cuando se envía un mensaje nuevo
                }
            });
        }
    }, []);

    // Ajustar altura cuando cambia el texto
    useEffect(() => {
        if (messageText !== '') {
            adjustTextareaHeight();
        } else if (textareaRef.current) {
            // Resetear a altura mínima cuando está vacío
            textareaRef.current.style.height = '48px';
        }
    }, [messageText, adjustTextareaHeight]);

    // Ajustar altura cuando el componente se monta o cuando cambia el contacto
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = '48px';
            adjustTextareaHeight();
        }
    }, [selectedContactId, adjustTextareaHeight]);

    const renderContact = (contact) => (
        <button
            type="button"
            key={contact.id}
            className={`chat-contact-row ${selectedContactId === contact.id ? 'selected' : ''}`}
            onClick={() => setSelectedContactId(contact.id)}
        >
            <div className="chat-contact-avatar" style={{ backgroundColor: contact.avatarColor }}>
                {contact.initials}
            </div>
            <div className="chat-contact-info">
                <div className="chat-contact-top">
                    <span className="chat-contact-name">{contact.displayName}</span>
                    {contact.unreadCount > 0 && (
                        <span className="chat-unread-badge">{contact.unreadCount}</span>
                    )}
                </div>
                <div className="chat-contact-bottom">
                    <span className="chat-contact-house">{contact.houseLabel}</span>
                    <span className={`chat-contact-status ${contact.isOnline ? 'online' : 'offline'}`}>
                        <FaCircle />
                        {contact.isOnline ? 'En línea' : contact.statusLabel}
                    </span>
                </div>
            </div>
        </button>
    );

    const cardClassName = selectedContact ? 'chat-comunitario-card contact-selected' : 'chat-comunitario-card';

    return (
        <div className="chat-comunitario-page">
            <div className={cardClassName}>
                <aside className="chat-sidebar">
                    <div className="chat-sidebar-header">
                        <h2>Chat Comunitario</h2>
                        <p>
                            {currentUserName}
                            {' '}
                            •
                            {' '}
                            {currentUserHouse}
                        </p>
                    </div>
                    <div className="chat-search">
                        <FaSearch className="chat-search-icon" />
                        <input
                            type="text"
                            placeholder="Buscar residentes..."
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            disabled={!fraccionamientoId}
                        />
                    </div>
                    <div className="chat-contact-groups">
                        {fraccionamientoId ? (
                            <>
                                <div className="chat-contact-group">
                                    <div className="chat-contact-group-header">
                                        <span>
                                            En línea
                                            {' '}
                                            (
                                            {onlineContacts.length}
                                            )
                                        </span>
                                    </div>
                                    <div className="chat-contact-list">
                                        {onlineContacts.length === 0 && (
                                            <p className="chat-empty-message">No hay residentes en línea en este momento.</p>
                                        )}
                                        {onlineContacts.map(renderContact)}
                                    </div>
                                </div>
                                <div className="chat-contact-group">
                                    <div className="chat-contact-group-header">
                                        <span>
                                            Fuera de línea
                                            {' '}
                                            (
                                            {offlineContacts.length}
                                            )
                                        </span>
                                    </div>
                                    <div className="chat-contact-list">
                                        {offlineContacts.length === 0 && (
                                            <p className="chat-empty-message">Todos tus contactos están activos.</p>
                                        )}
                                        {offlineContacts.map(renderContact)}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="chat-contact-group">
                                <div className="chat-contact-list">
                                    <p className="chat-empty-message">No hay fraccionamiento asignado.</p>
                                </div>
                            </div>
                        )}
                    </div>
                </aside>
                <section className="chat-main">
                    {selectedContact ? (
                        <>
                            <header className="chat-main-header">
                                <div className="chat-main-header-left">
                                    <button
                                        type="button"
                                        className="chat-back-button"
                                        onClick={() => setSelectedContactId(null)}
                                        aria-label="Volver a la lista de contactos"
                                    >
                                        <FaChevronLeft />
                                    </button>
                                    <div className="chat-contact-avatar large" style={{ backgroundColor: selectedContact.avatarColor }}>
                                        {selectedContact.initials}
                                    </div>
                                    <div className="chat-main-contact-meta">
                                        <h3>{selectedContact.displayName}</h3>
                                        <p>
                                            {selectedContact.houseLabel}
                                            {' '}
                                            •
                                            {' '}
                                            {selectedContact.isOnline ? 'En línea' : selectedContact.statusLabel}
                                        </p>
                                    </div>
                                </div>
                            </header>
                            <div className="chat-messages" id="chat-messages" ref={messagesContainerRef}>
                                {messages.length === 0 ? (
                                    <div className="chat-empty-conversation">No hay mensajes aún. ¡Envía el primero!</div>
                                ) : (
                                    messages.map((message) => (
                                        <div
                                            key={message.id}
                                            className={`chat-message ${message.senderId === currentUid ? 'outgoing' : 'incoming'}`}
                                        >
                                            <div className="chat-bubble">
                                                <p>{message.text}</p>
                                                <span className="chat-message-time">{formatMessageTime(message.createdAt)}</span>
                                            </div>
                                        </div>
                                    ))
                                )}
                                <div ref={messagesEndRef} />
                            </div>
                            <form className="chat-composer" onSubmit={handleSendMessage}>
                                <textarea
                                    ref={textareaRef}
                                    placeholder="Escribe un mensaje..."
                                    value={messageText}
                                    onChange={(event) => {
                                        setMessageText(event.target.value);
                                        // Ajustar altura después de actualizar el estado, sin hacer scroll
                                        requestAnimationFrame(() => {
                                            adjustTextareaHeight();
                                        });
                                    }}
                                    onKeyDown={handleKeyDown}
                                    onInput={adjustTextareaHeight}
                                    rows={1}
                                    autoCorrect="on"
                                    spellCheck="true"
                                    autoCapitalize="sentences"
                                />
                                <button type="submit" disabled={!messageText.trim()}>
                                    <FaPaperPlane />
                                    <span>Enviar</span>
                                </button>
                            </form>
                        </>
                    ) : (
                        <div className="chat-placeholder">
                            <div className="chat-placeholder-icon">
                                <FaUserCircle />
                            </div>
                            <h3>Selecciona un contacto</h3>
                            <p>Elige un residente o a la administración para comenzar a chatear.</p>
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
};

export default ChatComunitario;
